import React from "react";
import styled from "styled-components";
import { skills } from "../../data/constants";
import { css } from "styled-components";
import { motion } from "framer-motion";
import { fadeUpVariants, staggerContainer } from "../../App";

// Define a cursor pointer class
// eslint-disable-next-line no-unused-vars
const cursorPointer = css`
  cursor: pointer;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 0;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 20px;
  padding: 0 20px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

export const Title = styled.div`
  font-size: 48px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  background: linear-gradient(135deg, ${({ theme }) => theme.text_primary} 0%, ${({ theme }) => theme.primary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;
  }
`;

export const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SkillsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 40px;
  gap: 30px;
  justify-content: center;
`;

const Skill = styled.div`
  width: 100%;
  max-width: 500px;
  background: rgba(23, 23, 33, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(133, 76, 230, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  padding: 24px 36px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: rgba(133, 76, 230, 0.6);
    box-shadow: 0 20px 40px rgba(133, 76, 230, 0.2),
                0 0 60px rgba(133, 76, 230, 0.1);
  }
  
  @media (max-width: 768px) {
    max-width: 400px;
    padding: 18px 28px;
  }
  @media (max-width: 500px) {
    max-width: 330px;
    padding: 16px 24px;
  }
`;

const SkillTitle = styled.h2`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 24px;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
    border-radius: 2px;
  }
`;

const SkillList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 16px;
`;

const SkillItem = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_primary};
  border: 1px solid rgba(133, 76, 230, 0.3);
  background: rgba(133, 76, 230, 0.05);
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(133, 76, 230, 0.15);
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(133, 76, 230, 0.2);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 14px;
  }
`;

const SkillImage = styled.img`
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
  
  ${SkillItem}:hover & {
    transform: scale(1.2);
  }
`;

const Skills = () => {
  return (
    <Container id="skills">
      <Wrapper
        as={motion.div}
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <motion.div variants={fadeUpVariants}>
          <Title>Skills</Title>
        </motion.div>
        <motion.div variants={fadeUpVariants}>
          <Desc>
            Here are some of my skills on which I have been working on for the
            past 2 years.
          </Desc>
        </motion.div>
        <SkillsContainer>
          {skills.map((skill, index) => (
            <motion.div
              key={index}
              variants={fadeUpVariants}
            >
              <Skill>
                <SkillTitle>{skill.title}</SkillTitle>
                <SkillList>
                  {skill.skills.map((item, idx) => (
                    <SkillItem key={idx}>
                      <SkillImage src={item.image} />
                      {item.name}
                    </SkillItem>
                  ))}
                </SkillList>
              </Skill>
            </motion.div>
          ))}
        </SkillsContainer>
      </Wrapper>
    </Container>
  );
};

export default Skills;
